import { useState } from 'react';
import {
  AlignLeft,
  Building2,
  Calendar,
  ChevronRight,
  CornerDownRight,
  DollarSign,
  Hash,
  Link as LinkIcon,
  ListChecks,
  Mail,
  PenLine,
  Type,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { resolveFieldHints } from '@/lib/docx/sub-field-hints';
import { useDocxSelection, type MirrorEntry } from './context';
import { COMPANY_SUB_ICON, SUB_ICON, type MarkedVariable, type ScalarType } from './types';

const TYPE_ICON: Record<ScalarType, LucideIcon> = {
  text: Type,
  textarea: AlignLeft,
  number: Hash,
  date: Calendar,
  currency: DollarSign,
  select: ListChecks,
  email: Mail,
  url: LinkIcon,
};

function DetailLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-[11px]">
      <span className="text-globe-grey shrink-0 w-20">{label}</span>
      <span className="text-[hsl(var(--fg))] min-w-0 wrap-break-word">{value}</span>
    </div>
  );
}

function VariableDetails({ v }: { v: MarkedVariable }) {
  // Person and company sub-fields carry no stored hints — their slot supplies them at render
  // time. Resolve here so this preview shows exactly what the fill-in form will show.
  const hints = resolveFieldHints(v);
  return (
    <div className="space-y-1 border-t border-border px-3 py-2 bg-muted/30">
      {v.valueCopyFrom && <DetailLine label="Mirrors" value={v.valueCopyFrom} />}
      <DetailLine label="Type" value={v.type} />
      {v.type === 'currency' && <DetailLine label="Currency" value={v.currencyCode || '—'} />}
      <DetailLine label="Required" value={v.required ? 'Yes' : 'No'} />
      {v.options && v.options.length > 0 && (
        <DetailLine label="Choices" value={v.options.join(', ')} />
      )}
      <DetailLine label="Placeholder" value={hints.placeholder || '—'} />
      <DetailLine label="Help text" value={hints.helpText || '—'} />
      {v.selectedText && <DetailLine label="Marked text" value={v.selectedText} />}
      <DetailLine label="Reference" value={v.name} />
    </div>
  );
}

/** Nested mirror (valueCopyFrom) fields shown under their source leaf, collapsible. */
function MirrorOverviewList({
  mirrors,
  expanded,
  toggle,
  pl,
}: {
  mirrors: MirrorEntry[];
  expanded: Set<string>;
  toggle: (key: string) => void;
  pl: string;
}) {
  return (
    <ul className="border-t border-border bg-muted/20">
      {mirrors.map((m) => {
        const Icon = TYPE_ICON[m.v.type] ?? Type;
        const open = expanded.has(m.v.name);
        return (
          <li key={m.v.name}>
            <button
              type="button"
              onClick={() => toggle(m.v.name)}
              aria-expanded={open}
              className={`flex w-full items-center gap-2 ${pl} pr-3 py-2 text-left hover:bg-muted transition-colors`}
            >
              <ChevronRight
                className={`w-3.5 h-3.5 text-globe-grey shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
              />
              <CornerDownRight className="w-3 h-3 text-globe-grey shrink-0" />
              <Icon className="w-3.5 h-3.5 text-globe-grey shrink-0" />
              <span className="text-sm text-[hsl(var(--fg))] truncate flex-1">{m.v.label}</span>
              <span className="text-[10px] text-globe-grey shrink-0">mirror</span>
            </button>
            {open && <VariableDetails v={m.v} />}
          </li>
        );
      })}
    </ul>
  );
}

/** Read-only, collapsible summary of the marked fields — shown on the Publish step canvas. */
export function VariableOverview() {
  const { renderRows } = useDocxSelection();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (renderRows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-globe-grey">
        No fields marked yet. Go back to “Mark fields” to add fill-in fields.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-[hsl(var(--fg))]">
        Fields in this template ({renderRows.length})
      </h3>
      <ul className="space-y-2">
        {renderRows.map((row) => {
          if (row.kind === 'field') {
            const Icon = TYPE_ICON[row.v.type] ?? Type;
            const open = expanded.has(row.v.name);
            return (
              <li
                key={row.v.name}
                className="rounded-lg border border-border bg-card overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggle(row.v.name)}
                  aria-expanded={open}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted transition-colors"
                >
                  <ChevronRight
                    className={`w-4 h-4 text-globe-grey shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
                  />
                  <div className="w-7 h-7 rounded bg-selise-blue/10 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-selise-blue" />
                  </div>
                  <span className="text-sm font-medium text-[hsl(var(--fg))] truncate flex-1">
                    {row.v.label}
                  </span>
                  {row.v.required && (
                    <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0">
                      Required
                    </Badge>
                  )}
                  <span className="text-[11px] text-globe-grey shrink-0">{row.v.type}</span>
                </button>
                {open && <VariableDetails v={row.v} />}
                {open && row.mirrors.length > 0 && (
                  <MirrorOverviewList
                    mirrors={row.mirrors}
                    expanded={expanded}
                    toggle={toggle}
                    pl="pl-9"
                  />
                )}
              </li>
            );
          }

          // Person / company group — collapsible header, each child collapsible inside.
          const groupOpen = expanded.has(row.meta.key);
          const isCompany = row.kind === 'company';
          return (
            <li
              key={row.meta.key}
              className="rounded-lg border border-border bg-card overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggle(row.meta.key)}
                aria-expanded={groupOpen}
                className="flex w-full items-center gap-3 bg-muted/50 px-3 py-2.5 text-left hover:bg-muted transition-colors"
              >
                <ChevronRight
                  className={`w-4 h-4 text-globe-grey shrink-0 transition-transform ${groupOpen ? 'rotate-90' : ''}`}
                />
                <div className="w-7 h-7 rounded bg-selise-blue/10 flex items-center justify-center shrink-0">
                  {isCompany ? (
                    <Building2 className="w-4 h-4 text-selise-blue" />
                  ) : (
                    <UserRound className="w-4 h-4 text-selise-blue" />
                  )}
                </div>
                <span className="text-sm font-medium text-[hsl(var(--fg))] truncate flex-1">
                  {row.meta.label}
                </span>
                {row.kind === 'person' && row.meta.isSignatory && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-selise-blue/10 px-1.5 py-0.5 text-[10px] font-medium text-selise-blue shrink-0">
                    <PenLine className="w-2.5 h-2.5" />
                    signs · {row.meta.signingOrder ?? 1}
                  </span>
                )}
                <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
                  {isCompany ? 'Company' : 'Person'} · {row.children.length}
                </Badge>
              </button>
              {groupOpen && (
                <ul className="divide-y divide-border border-t border-border">
                  {row.children.map((c) => {
                    const Icon =
                      (c.v.companySubKind && COMPANY_SUB_ICON[c.v.companySubKind]) ||
                      (c.v.subKind && SUB_ICON[c.v.subKind]) ||
                      Type;
                    const open = expanded.has(c.v.name);
                    return (
                      <li key={c.v.name}>
                        <button
                          type="button"
                          onClick={() => toggle(c.v.name)}
                          aria-expanded={open}
                          className="flex w-full items-center gap-2 px-3 py-2 pl-5 text-left hover:bg-muted transition-colors"
                        >
                          <ChevronRight
                            className={`w-3.5 h-3.5 text-globe-grey shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
                          />
                          <Icon className="w-3.5 h-3.5 text-globe-grey shrink-0" />
                          <span className="text-sm text-[hsl(var(--fg))] truncate flex-1">
                            {c.v.label}
                          </span>
                          {c.v.required && (
                            <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0">
                              Required
                            </Badge>
                          )}
                        </button>
                        {open && <VariableDetails v={c.v} />}
                        {open && c.mirrors.length > 0 && (
                          <MirrorOverviewList
                            mirrors={c.mirrors}
                            expanded={expanded}
                            toggle={toggle}
                            pl="pl-11"
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
